// ============================================
// CoverImageTemplateUploader.js - WITH ADMIN PROTECTION
// React JS - Direct upload to B2 using signed URLs
// Now includes Supabase admin authentication
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import supabase from '../services/supabase'; // Your Supabase client
import { getEventTypeTags } from './eventTypeTags'; // Import tag helper
import './CoverImageTemplateUploader.css';

// Event types list
const EVENT_TYPES = [
  'Anniversary',
  'Annual Function',
  'Art Exhibition',
  'Baby Naming Ceremony',
  'Baby Shower',
  'BBQ',
  'Beach Party',
  'Birthday Party',
  'Book Club Meetup',
  'Bridal Shower',
  'Camping',
  'Charity Event',
  'Christmas Party',
  'Cocktail Party',
  'Community Gathering',
  'Conference',
  'Concert',
  'Cultural Event',
  'Diwali',
  'Dinner Party',
  'Eid',
  'Engagement Party',
  'Farewell Party',
  'Festival',
  'Festival Afterparty',
  'Freshers Party',
  'Fundraiser',
  'Game Night',
  'Graduation',
  'Haldi',
  'Halloween Party',
  'Hiking',
  'Holiday Celebration',
  'Holi',
  'Housewarming',
  'Jagraata',
  'Katha',
  'Karaoke Night',
  'Launch Party',
  'Meet & Greet',
  'Mehendi',
  'Movie Night',
  'Music Night',
  'Networking Event',
  'New Year Party',
  'Office Party',
  'Open Mic',
  'Paath',
  'Photo Walk',
  'Picnic',
  'Pool Party',
  'Pooja',
  'Potluck',
  'Reunion',
  'Religious Ceremony',
  'Road Trip',
  'Sangeet',
  'Seminar',
  'Sports Viewing',
  'Startup Pitch Event',
  'Wedding',
  'Workshop',
  'Wellness Session',
  'Yoga Session',
];

const CDN_URL = 'https://cdn.invits.co';
const CLOUD_FUNCTION_URL = 'https://asia-south1-invits-app.cloudfunctions.net';

// ============================================
// ADMIN PROTECTION WRAPPER
// ============================================
const CoverImageTemplateUploader = () => {
  // Auth state
  const [authState, setAuthState] = useState({
    loading: true,
    user: null,
    isAdmin: false,
    error: null,
  });

  // Check admin access on mount
  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        setAuthState({
          loading: false,
          user: null,
          isAdmin: false,
          error: 'Please log in to access this page',
        });
        return;
      }

      // Check if user is in admins table
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('user_id, email, permissions')
        .eq('user_id', user.id)
        .single();

      if (adminError || !adminData) {
        setAuthState({
          loading: false,
          user,
          isAdmin: false,
          error: 'Access denied. Admin privileges required.',
        });
        return;
      }

      // User is admin
      setAuthState({
        loading: false,
        user,
        isAdmin: true,
        error: null,
      });
    } catch (err) {
      console.error('Admin check error:', err);
      setAuthState({
        loading: false,
        user: null,
        isAdmin: false,
        error: 'Error checking admin access',
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login'; // Redirect to login page
  };

  // Loading state
  if (authState.loading) {
    return (
      <div className="auth-container">
        <div className="auth-loading-box">
          <div className="auth-spinner"></div>
          <p className="auth-loading-text">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Access denied state
  if (!authState.isAdmin) {
    return (
      <div className="auth-container">
        <div className="auth-error-box">
          <h1 className="auth-error-title">🔒 Access Restricted</h1>
          <p className="auth-error-text">
            {authState.error || 'You do not have permission to access this page.'}
          </p>
          <div className="auth-error-details">
            <p>This page is only accessible to administrators.</p>
            {!authState.user ? (
              <a href="/login" className="auth-login-link">
                Go to Login
              </a>
            ) : (
              <div className="auth-user-info">
                <p>Logged in as: <strong>{authState.user.email}</strong></p>
                <p className="auth-hint">
                  Your account does not have admin privileges. Contact the administrator if you believe this is an error.
                </p>
                <button onClick={handleSignOut} className="auth-signout-btn">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Admin access granted - show uploader
  return (
    <div>
      {/* Admin header bar */}
      <div className="admin-bar">
        <div className="admin-info">
          <span className="admin-badge">✅ Admin Access</span>
          <span className="admin-email">{authState.user.email}</span>
        </div>
        <button onClick={handleSignOut} className="admin-signout-btn">
          Sign Out
        </button>
      </div>

      {/* Main uploader component */}
      <UploaderComponent user={authState.user} />
    </div>
  );
};

// ============================================
// MAIN UPLOADER COMPONENT (Original Logic)
// ============================================
const UploaderComponent = ({ user }) => {
  const [coverType, setCoverType] = useState('invitation');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [batchSettings, setBatchSettings] = useState({
    eventType: '',
    customKeywords: [], // Only custom keywords here
  });
  const [keywordInput, setKeywordInput] = useState('');
  const fileInputRef = useRef(null);

  // Update files' auto-tags when batch event type changes
  useEffect(() => {
    if (batchSettings.eventType) {
      const autoTags = getEventTypeTags(batchSettings.eventType);
      setSelectedFiles((prev) =>
        prev.map((f) => ({
          ...f,
          autoKeywords: autoTags,
        }))
      );
    }
  }, [batchSettings.eventType]);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not an image file`);
          return null;
        }

        const preview = await createImagePreview(file);
        const dimensions = await getImageDimensions(preview);

        const autoTags = batchSettings.eventType 
          ? getEventTypeTags(batchSettings.eventType) 
          : [];

        return {
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview,
          name: file.name,
          size: file.size,
          width: dimensions.width,
          height: dimensions.height,
          eventType: batchSettings.eventType || '',
          autoKeywords: autoTags, // Auto-generated tags
          customKeywords: [...(batchSettings.customKeywords || [])], // User-added tags
          status: 'pending',
          error: null,
        };
      })
    );

    setSelectedFiles((prev) => [
      ...prev,
      ...processedFiles.filter(Boolean),
    ]);
  };

  // Create image preview
  const createImagePreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  };

  // Get image dimensions
  const getImageDimensions = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = src;
    });
  };

  // Update file settings
  const updateFileSettings = (fileId, updates) => {
    setSelectedFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
    );
  };

  // Remove file
  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Add keyword to file
  const addFileKeyword = (fileId, keyword) => {
    if (!keyword.trim()) return;
    
    setSelectedFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? {
              ...f,
              customKeywords: [...new Set([...(f.customKeywords || []), keyword.trim()])],
            }
          : f
      )
    );
  };

  // Remove keyword from file
  const removeFileKeyword = (fileId, keyword) => {
    setSelectedFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? {
              ...f,
              customKeywords: (f.customKeywords || []).filter((k) => k !== keyword),
            }
          : f
      )
    );
  };

  // Apply batch settings to all files
  const applyBatchSettings = () => {
    setSelectedFiles((prev) =>
      prev.map((f) => {
        const autoTags = batchSettings.eventType 
          ? getEventTypeTags(batchSettings.eventType) 
          : f.autoKeywords || [];
        
        return {
          ...f,
          eventType: batchSettings.eventType || f.eventType,
          autoKeywords: autoTags,
          customKeywords: [...new Set([...(f.customKeywords || []), ...(batchSettings.customKeywords || [])])],
        };
      })
    );
  };

  // Add batch keyword
  const addBatchKeyword = () => {
    if (!keywordInput.trim()) return;
    
    setBatchSettings((prev) => ({
      ...prev,
      customKeywords: [...new Set([...(prev.customKeywords || []), keywordInput.trim()])],
    }));
    setKeywordInput('');
  };

  // Remove batch keyword
  const removeBatchKeyword = (keyword) => {
    setBatchSettings((prev) => ({
      ...prev,
      customKeywords: (prev.customKeywords || []).filter((k) => k !== keyword),
    }));
  };

  // Convert PNG to WebP
  const convertToWebP = async (file, quality = 0.92) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP conversion failed'));
              }
            },
            'image/webp',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Create thumbnail
  const createThumbnail = async (file, maxWidth = 360) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const ratio = maxWidth / img.width;
          const canvas = document.createElement('canvas');
          canvas.width = maxWidth;
          canvas.height = img.height * ratio;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Thumbnail creation failed'));
              }
            },
            'image/webp',
            0.85
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload to B2
  const uploadToB2 = async (blob, uploadUrl, authToken, fileName, contentType) => {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': authToken,
        'X-Bz-File-Name': encodeURIComponent(fileName),
        'Content-Type': contentType,
        'X-Bz-Content-Sha1': 'do_not_verify',
      },
      body: blob,
    });

    if (!response.ok) {
      throw new Error(`B2 upload failed: ${response.statusText}`);
    }

    return await response.json();
  };


  // Upload all files
const handleUploadAll = async () => {
    const pendingFiles = selectedFiles.filter((f) => f.status === 'pending');
    
    if (pendingFiles.length === 0) {
      alert('No files to upload');
      return;
    }

    // Validate all files have event type
    const missingEventType = pendingFiles.some((f) => !f.eventType);
    if (missingEventType) {
      alert('Please select an event type for all files');
      return;
    }

    setUploading(true);

    try {
      console.log(`🚀 Starting batch upload for ${pendingFiles.length} files`);
      
      // 1. Get auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication required. Please log in again.');
      }

      // 2. Convert all files and prepare upload list
      console.log('🔄 Converting all files...');
      const allFilesToUpload = [];
      
      for (const fileData of pendingFiles) {
        try {
          // Update status
          setSelectedFiles((prev) =>
            prev.map((f) => (f.id === fileData.id ? { ...f, status: 'converting' } : f))
          );

          const timestamp = Date.now();
          const uuid = Math.random().toString(36).substr(2, 9);

          // Convert to WebP and create thumbnail
          const webpBlob = await convertToWebP(fileData.file, 0.92);
          const thumbBlob = await createThumbnail(fileData.file, 360);

          // Add all 3 versions to the upload list
          allFilesToUpload.push(
            {
              originalFileData: fileData,
              fileName: `${uuid}_original_${timestamp}.png`,
              blob: fileData.file,
              fileSize: fileData.file.size,
              contentType: 'image/png',
              type: 'original',
              uuid,
              timestamp,
            },
            {
              originalFileData: fileData,
              fileName: `${uuid}_webp_${timestamp}.webp`,
              blob: webpBlob,
              fileSize: webpBlob.size,
              contentType: 'image/webp',
              type: 'webp',
              uuid,
              timestamp,
            },
            {
              originalFileData: fileData,
              fileName: `${uuid}_thumb_${timestamp}.webp`,
              blob: thumbBlob,
              fileSize: thumbBlob.size,
              contentType: 'image/webp',
              type: 'thumb',
              uuid,
              timestamp,
            }
          );

          console.log(`✅ Converted: ${fileData.name}`);
        } catch (error) {
          console.error(`❌ Error converting ${fileData.name}:`, error);
          setSelectedFiles((prev) =>
            prev.map((f) =>
              f.id === fileData.id
                ? { ...f, status: 'error', error: `Conversion failed: ${error.message}` }
                : f
            )
          );
        }
      }

      console.log(`📦 Total files to upload: ${allFilesToUpload.length}`);

      // 3. Get upload URLs for ALL files in one batch API call
      console.log('🌐 Getting batch upload URLs...');
      const batchResponse = await fetch(`${CLOUD_FUNCTION_URL}/getBatchUploadUrls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          files: allFilesToUpload.map(f => ({
            fileName: f.fileName,
            fileSize: f.fileSize,
            contentType: f.contentType,
          })),
          context: 'template_upload',
        }),
      });

      if (!batchResponse.ok) {
        const errorText = await batchResponse.text();
        throw new Error(`Failed to get batch upload URLs: ${errorText}`);
      }

      const { uploadUrls, successful, failed } = await batchResponse.json();
      console.log(`✅ Got ${successful} upload URLs (${failed} failed)`);

      if (failed > 0) {
        console.warn(`⚠️ ${failed} files failed to get upload URLs`);
      }

      // 4. Map upload URLs back to file blobs
      const filesToUploadWithUrls = allFilesToUpload.map((file, index) => ({
        ...file,
        uploadUrl: uploadUrls[index]?.uploadUrl,
        authToken: uploadUrls[index]?.authToken,
        uploadSuccess: uploadUrls[index]?.success,
      }));

      // 5. Update all files to "uploading" status
      pendingFiles.forEach(fileData => {
        setSelectedFiles((prev) =>
          prev.map((f) => (f.id === fileData.id ? { ...f, status: 'uploading' } : f))
        );
      });

      // 6. Upload all files to B2 in parallel (with concurrency limit)
      console.log('📤 Uploading to B2...');
      const CONCURRENT_UPLOADS = 10; // Upload 10 files at a time
      
      for (let i = 0; i < filesToUploadWithUrls.length; i += CONCURRENT_UPLOADS) {
        const batch = filesToUploadWithUrls.slice(i, i + CONCURRENT_UPLOADS);
        
        await Promise.all(
          batch.map(async (file) => {
            if (!file.uploadSuccess || !file.uploadUrl) {
              console.error(`❌ Skipping ${file.fileName} - no upload URL`);
              return;
            }

            try {
              await uploadToB2(
                file.blob,
                file.uploadUrl,
                file.authToken,
                file.fileName,
                file.contentType
              );
              console.log(`✅ Uploaded: ${file.fileName}`);
            } catch (error) {
              console.error(`❌ Failed to upload ${file.fileName}:`, error);
              throw error;
            }
          })
        );
      }

      console.log('✅ All files uploaded to B2');

      // 7. Group files by original file and save to database
      console.log('💾 Saving metadata to Supabase...');
      const fileGroups = {};
      
      filesToUploadWithUrls.forEach(file => {
        const key = file.uuid;
        if (!fileGroups[key]) {
          fileGroups[key] = {
            original: null,
            webp: null,
            thumb: null,
            fileData: file.originalFileData,
          };
        }
        fileGroups[key][file.type] = file.fileName;
      });

      // Insert all records in batch
      const dbRecords = Object.values(fileGroups).map(group => {
        const folderPath = `templates/${coverType}/${group.fileData.eventType.toLowerCase().replace(/ /g, '-')}`;
        const allKeywords = [
          ...(group.fileData.autoKeywords || []),
          ...(group.fileData.customKeywords || []),
        ];

        return {
          cover_type: coverType,
          event_type: group.fileData.eventType,
          file_name: group.fileData.name,
          original_url: `${CDN_URL}/${folderPath}/${group.original}`,
          webp_url: `${CDN_URL}/${folderPath}/${group.webp}`,
          thumbnail_url: `${CDN_URL}/${folderPath}/${group.thumb}`,
          keywords: allKeywords,
          width: group.fileData.width,
          height: group.fileData.height,
          file_size: group.fileData.size,
          uploaded_by: user.id,
        };
      });

      const { error: dbError } = await supabase
        .from('cover_image_templates')
        .insert(dbRecords);

      if (dbError) throw dbError;

      console.log('✅ All metadata saved to database');

      // 8. Update all files to success
      pendingFiles.forEach(fileData => {
        setSelectedFiles((prev) =>
          prev.map((f) => (f.id === fileData.id ? { ...f, status: 'success' } : f))
        );
      });

      alert(`🎉 Successfully uploaded ${pendingFiles.length} templates!`);
    } catch (error) {
      console.error('❌ Batch upload error:', error);
      alert(`Upload failed: ${error.message}\n\nCheck console for details.`);
      
      // Mark all uploading files as error
      setSelectedFiles((prev) =>
        prev.map((f) =>
          f.status === 'uploading' || f.status === 'converting'
            ? { ...f, status: 'error', error: error.message }
            : f
        )
      );
    } finally {
      setUploading(false);
    }
  };

  // Clear successful uploads
  const clearSuccessful = () => {
    setSelectedFiles((prev) => prev.filter((f) => f.status !== 'success'));
  };

  return (
    <div className="uploader-container">
      <div className="uploader-header">
        <h1>📸 Cover Image Template Uploader</h1>
        <p>Upload templates with auto-generated tags</p>
      </div>

      {/* Cover Type Selection */}
      <div className="cover-type-section">
        <label>Cover Type:</label>
        <div className="cover-type-options">
          <button
            className={coverType === 'invitation' ? 'active' : ''}
            onClick={() => setCoverType('invitation')}
          >
            📨 Invitation
          </button>
          <button
            className={coverType === 'greeting' ? 'active' : ''}
            onClick={() => setCoverType('greeting')}
          >
            💌 Greeting
          </button>
        </div>
      </div>

      {/* Batch Settings */}
      <div className="batch-settings">
        <h3>⚙️ Batch Settings (Apply to All)</h3>
        
        <div className="batch-field">
          <label>Event Type:</label>
          <select
            value={batchSettings.eventType}
            onChange={(e) =>
              setBatchSettings({ ...batchSettings, eventType: e.target.value })
            }
            disabled={uploading}
          >
            <option value="">Select Event Type</option>
            {EVENT_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {batchSettings.eventType && (
          <div className="auto-tags-preview">
            <strong>🤖 Auto-tags for "{batchSettings.eventType}":</strong>
            <div className="tags-list">
              {getEventTypeTags(batchSettings.eventType).slice(0, 10).map((tag) => (
                <span key={tag} className="tag auto-tag">{tag}</span>
              ))}
              {getEventTypeTags(batchSettings.eventType).length > 10 && (
                <span className="tag more-tag">
                  +{getEventTypeTags(batchSettings.eventType).length - 10} more
                </span>
              )}
            </div>
          </div>
        )}

        <div className="batch-field">
          <label>Custom Keywords (optional):</label>
          <div className="keyword-input-group">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addBatchKeyword()}
              placeholder="Add custom keyword"
              disabled={uploading}
            />
            <button onClick={addBatchKeyword} disabled={uploading}>
              Add
            </button>
          </div>
          <div className="keywords-list">
            {(batchSettings.customKeywords || []).map((keyword) => (
              <span key={keyword} className="keyword-tag custom-tag">
                {keyword}
                <button onClick={() => removeBatchKeyword(keyword)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <button
          className="apply-batch-btn"
          onClick={applyBatchSettings}
          disabled={uploading || selectedFiles.length === 0}
        >
          Apply to All Files
        </button>
      </div>

      {/* File Selection */}
      <div className="file-selection">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        <button
          className="select-files-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          + Select Images
        </button>
        <span className="file-count">
          {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
        </span>
      </div>

      {/* Files Grid */}
      {selectedFiles.length > 0 && (
        <div className="files-section">
          <div className="files-header">
            <h3>Selected Files</h3>
            <div className="files-actions">
              <button
                className="clear-btn"
                onClick={clearSuccessful}
                disabled={uploading || !selectedFiles.some((f) => f.status === 'success')}
              >
                Clear Successful
              </button>
              <button
                className="upload-all-btn"
                onClick={handleUploadAll}
                disabled={uploading || selectedFiles.every((f) => f.status !== 'pending')}
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            </div>
          </div>

          <div className="files-grid">
            {selectedFiles.map((fileData) => (
              <FileCard
                key={fileData.id}
                fileData={fileData}
                onUpdateSettings={updateFileSettings}
                onRemove={removeFile}
                onAddKeyword={addFileKeyword}
                onRemoveKeyword={removeFileKeyword}
                uploading={uploading}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// FILE CARD COMPONENT
// ============================================
const FileCard = ({
  fileData,
  onUpdateSettings,
  onRemove,
  onAddKeyword,
  onRemoveKeyword,
  uploading,
}) => {
  const [keywordInput, setKeywordInput] = useState('');

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      onAddKeyword(fileData.id, keywordInput.trim());
      setKeywordInput('');
    }
  };

  const getStatusColor = () => {
    switch (fileData.status) {
      case 'pending': return '#666';
      case 'uploading': return '#2196F3';
      case 'success': return '#4CAF50';
      case 'error': return '#F44336';
      default: return '#666';
    }
  };

  const getStatusIcon = () => {
    switch (fileData.status) {
      case 'pending': return '⏳';
      case 'uploading': return '⏫';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

 return (
    <div className="file-card" style={{ borderColor: getStatusColor() }}>
      <div className="file-preview">
        <img src={fileData.preview} alt={fileData.name} />
        <div className="file-status" style={{ backgroundColor: getStatusColor() }}>
          {getStatusIcon()} {fileData.status}
        </div>
        {!uploading && fileData.status !== 'uploading' && (
          <button className="remove-btn" onClick={() => onRemove(fileData.id)}>×</button>
        )}
      </div>

      <div className="file-details">
        <div className="file-name" title={fileData.name}>{fileData.name}</div>
        <div className="file-info">
          {fileData.width} × {fileData.height} • {(fileData.size / 1024).toFixed(1)} KB
        </div>

        {fileData.error && (
          <div className="error-message">Error: {fileData.error}</div>
        )}

        <div className="file-settings">
          <label>Event Type:</label>
          <select
            value={fileData.eventType}
            onChange={(e) => {
              const newEventType = e.target.value;
              const autoTags = newEventType ? getEventTypeTags(newEventType) : [];
              onUpdateSettings(fileData.id, { 
                eventType: newEventType,
                autoKeywords: autoTags,
              });
            }}
            disabled={uploading || fileData.status === 'success'}
          >
            <option value="">Select Event Type</option>
            {EVENT_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="file-keywords">
          <label>Keywords/Tags:</label>
          
          {/* Auto-generated keywords */}
          {fileData.autoKeywords && fileData.autoKeywords.length > 0 && (
            <div className="keywords-section">
              <div className="keywords-section-label">
                🤖 Auto-generated ({fileData.autoKeywords.length} tags)
              </div>
              <div className="keywords-list">
                {fileData.autoKeywords.slice(0, 10).map((keyword) => (
                  <span key={keyword} className="keyword-tag auto-tag" title="Auto-generated from event type">
                    {keyword}
                  </span>
                ))}
                {fileData.autoKeywords.length > 10 && (
                  <span className="keyword-tag more-tag">
                    +{fileData.autoKeywords.length - 10} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Custom keywords */}
          <div className="keywords-section">
            <div className="keywords-section-label">
              ✏️ Custom Tags ({(fileData.customKeywords || []).length})
            </div>
            <div className="keyword-input-group">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                placeholder="Add custom keyword"
                disabled={uploading || fileData.status === 'success'}
              />
              <button
                onClick={handleAddKeyword}
                disabled={uploading || fileData.status === 'success'}
              >
                +
              </button>
            </div>
            <div className="keywords-list">
              {(fileData.customKeywords || []).map((keyword) => (
                <span key={keyword} className="keyword-tag custom-tag">
                  {keyword}
                  {fileData.status !== 'success' && (
                    <button
                      onClick={() => onRemoveKeyword(fileData.id, keyword)}
                      disabled={uploading}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ACCESS CONTROL STYLES
// ============================================

export default CoverImageTemplateUploader;